import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Apple } from "lucide-react";
import { getClientConfig } from "../../lib/apiClient";
import { useStore } from "../../stores/rootStore";

let applePromise: Promise<void> | null = null;
function loadAppleId(): Promise<void> {
	if (applePromise) return applePromise;
	applePromise = new Promise<void>((resolve, reject) => {
		if (window.AppleID?.auth) {
			resolve();
			return;
		}
		const script = document.createElement("script");
		script.src =
			"https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
		script.async = true;
		script.defer = true;
		script.onload = () => resolve();
		script.onerror = () => reject(new Error("Failed to load Apple Sign in"));
		document.head.appendChild(script);
	});
	return applePromise;
}

/** Apple Sign in appears when VITE_APPLE_CLIENT_ID is set. */
export function AppleSignInButton() {
	const { auth } = useStore();
	const { t } = useTranslation();
	const config = getClientConfig();
	const [available, setAvailable] = useState(false);

	useEffect(() => {
		if (!config.appleClientId) {
			setAvailable(false);
			return;
		}
		let cancelled = false;
		loadAppleId()
			.then(() => {
				const appleAuth = window.AppleID?.auth;
				if (!appleAuth) throw new Error("Apple Sign in did not initialize");
				appleAuth.init({
					clientId: config.appleClientId,
					scope: "name email",
					redirectURI: config.appleRedirectUri || window.location.origin,
					usePopup: true,
				});
				if (!cancelled) setAvailable(true);
			})
			.catch(() => {
				if (!cancelled) setAvailable(false);
			});
		return () => {
			cancelled = true;
		};
	}, [config.appleClientId, config.appleRedirectUri]);

	async function signIn() {
		if (!window.AppleID?.auth || auth.submitting) return;
		try {
			const response = await window.AppleID.auth.signIn();
			const identityToken = response.authorization.id_token;
			const authorizationCode = response.authorization.code;
			if (!identityToken || !authorizationCode) throw new Error(t("auth.appleNoToken"));
			const fullName = response.user?.name
				? {
					givenName: response.user.name.firstName,
					familyName: response.user.name.lastName,
				}
				: undefined;
			await auth.signInWithApple({
				identityToken,
				authorizationCode,
				fullName,
			});
		} catch (error) {
			if (isAppleAuthCancellation(error)) return;
			auth.setError(t("auth.appleFailed"));
		}
	}

	if (!config.appleClientId) return null;
	return (
		<button
			type="button"
			onClick={() => void signIn()}
			disabled={!available || auth.submitting}
			className="auth-provider-button"
		>
			<Apple size={18} fill="currentColor" strokeWidth={2.25} />
			{t("auth.appleContinue")}
		</button>
	);
}

function isAppleAuthCancellation(error: unknown): boolean {
	if (!error || typeof error !== "object") return false;
	const code = "error" in error ? String((error as { error?: unknown }).error) : "";
	return code === "popup_closed_by_user" || code === "user_cancelled_authorize";
}
